import { type NextBestAction } from '@post-office/confluence-next-best-action';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { type JSX } from 'react/jsx-runtime';

// Match the backend's stable wire identifier, including messages created before productionization.
export const TEMPLATE_XFLOW_MESSAGE_KEY = 'twcg-2112-twc-xflow-conf-pages-templates' as const;

export type PostOfficeSideNavMessageKey = NextBestAction | typeof TEMPLATE_XFLOW_MESSAGE_KEY;

type PostOfficeSideNavMessageContextType = {
	enabledMessages: PostOfficeSideNavMessageKey[];
	enablePostOfficeSideNavMessage: (message: PostOfficeSideNavMessageKey) => void;
	disablePostOfficeSideNavMessage: (message: PostOfficeSideNavMessageKey) => void;
};

const PostOfficeSideNavMessageContext = createContext<PostOfficeSideNavMessageContextType | null>(
	null,
);

export const usePostOfficeSideNavMessages = (): PostOfficeSideNavMessageContextType | null => {
	return useContext(PostOfficeSideNavMessageContext);
};

type PostOfficeSideNavMessageProviderProps = {
	children: React.ReactNode;
};

export const PostOfficeSideNavMessageProvider = ({
	children,
}: PostOfficeSideNavMessageProviderProps): JSX.Element => {
	const [enabledMessages, setEnabledMessages] = useState<PostOfficeSideNavMessageKey[]>([]);

	const enablePostOfficeSideNavMessage = useCallback((message: PostOfficeSideNavMessageKey) => {
		setEnabledMessages((prev) => {
			if (prev.includes(message)) {
				return prev;
			}
			return [...prev, message];
		});
	}, []);
	const disablePostOfficeSideNavMessage = useCallback((message: PostOfficeSideNavMessageKey) => {
		setEnabledMessages((prev) =>
			prev.includes(message) ? prev.filter((enabledMessage) => enabledMessage !== message) : prev,
		);
	}, []);

	const value = useMemo(
		() => ({
			enabledMessages,
			enablePostOfficeSideNavMessage,
			disablePostOfficeSideNavMessage,
		}),
		[enabledMessages, enablePostOfficeSideNavMessage, disablePostOfficeSideNavMessage],
	);

	return (
		<PostOfficeSideNavMessageContext.Provider value={value}>
			{children}
		</PostOfficeSideNavMessageContext.Provider>
	);
};
